import { useEffect, useRef } from 'react';
import { HeadlessAuthDriver, useHeadlessOAuthWindow, reportHeadlessAuthError } from '../shared/headless';
import { useAuthConfigFetch } from '../shared/hooks/useAuthConfigFetch';
import { isOAuth } from '../shared/utils/oauthHelpers';
import { AuthenticateError } from '../shared/types/authErrors';
import { UnifiedAuthView } from './UnifiedAccountView';
import { jsx as _jsx } from "react/jsx-runtime";
/**
 * Headless presentation of the auth flow. For pure-OAuth apps (no input steps)
 * it drives the OAuth popup with no modal. When the flow turns out to need input
 * — or the config fails to load — it closes the popup that was opened on the
 * gesture and falls back to the regular modal (`UnifiedAuthView`).
 */
export function UnifiedHeadlessAuthView({
  portalId,
  appName,
  isReauth,
  accountId,
  onComplete,
  onCancel,
  onAuthError,
  fetchAuthConfig,
  useIntroScreen,
  useSuccessScreen,
  debug
}) {
  const oAuthWindow = useHeadlessOAuthWindow();
  const configFetchState = useAuthConfigFetch(fetchAuthConfig);
  const config = configFetchState.status === 'succeeded' ? configFetchState.config : undefined;
  const isHeadlessOAuth = config !== undefined && config.authFlow.steps.length === 0 && isOAuth(config.authFlow.type);
  const fellBackToModal = config !== undefined && !isHeadlessOAuth;
  const failed = configFetchState.status === 'failed';
  const configError = configFetchState.status === 'failed' ? configFetchState.err : undefined;
  const failureReportedRef = useRef(false);

  // This view is the single owner of the popup opened on the gesture. The
  // cleanup guarantees it is closed when the headless flow ends — on success,
  // failure, or a retry remount — including paths where the OAuth machine never
  // armed its own window cleanup (e.g. an authorize error before the popup is
  // navigated). The eager close below additionally dismisses it the moment we
  // decide to abandon it (fall back to the modal / config failed), since the
  // view itself stays mounted in the fallback case.
  useEffect(() => {
    return () => {
      oAuthWindow === null || oAuthWindow === void 0 || oAuthWindow.close();
    };
  }, [oAuthWindow]);
  useEffect(() => {
    if ((fellBackToModal || failed) && oAuthWindow) {
      oAuthWindow.close();
    }
  }, [fellBackToModal, failed, oAuthWindow]);
  useEffect(() => {
    if (failed && !failureReportedRef.current) {
      failureReportedRef.current = true;
      // Preserve the backend message and details when the config fetch rejected
      // with an AuthenticateError, so the headless alert reads the same as the
      // modal's fetch-failure state rather than the generic default copy.
      const reportedError = configError instanceof AuthenticateError ? configError.toAuthError() : {
        type: 'AuthenticateError'
      };
      reportHeadlessAuthError(reportedError, appName !== null && appName !== void 0 ? appName : '', onAuthError);
      // End the (modal-less) flow so the trigger's open state resets and a
      // re-click can start a fresh attempt.
      onCancel === null || onCancel === void 0 || onCancel();
    }
  }, [failed, configError, appName, onAuthError, onCancel]);
  if (configFetchState.status === 'pending' || failed) {
    return null;
  }
  if (isHeadlessOAuth && config) {
    return /*#__PURE__*/_jsx(HeadlessAuthDriver, {
      portalId: portalId,
      authConfig: config,
      onComplete: payload => onComplete === null || onComplete === void 0 ? void 0 : onComplete(payload),
      onError: onAuthError,
      onCancel: onCancel,
      oAuthWindow: oAuthWindow
    });
  }
  return /*#__PURE__*/_jsx(UnifiedAuthView, {
    isModal: true,
    isReauth: isReauth,
    portalId: portalId,
    onComplete: onComplete,
    onCancel: onCancel,
    onAuthError: onAuthError,
    fetchAuthConfig: fetchAuthConfig,
    accountId: accountId,
    useIntroScreen: useIntroScreen,
    useSuccessScreen: useSuccessScreen,
    debug: debug
  });
}